<template>
  <div class="LKPublicationsCardRecommendationAggregatedTable">
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="isLoading"
      :items-per-page="5"
      :footer-props="footerProps"
      no-data-text="..."
      dense
      class="mt-3 LKPublicationsCardRecommendationAggregatedTable__table"
    >
      <template #[`item.status`]="{ item }">
        {{ statusLabels[item.status] || item.status }}
      </template>

      <template #[`item.profile_type`]="{ item }">
        {{ profileTypeLabels[item.profile_type] || item.profile_type }}
      </template>

      <template #[`item.school_ids`]="{ item }">
        <div class="LKPublicationsCardRecommendationAggregatedTable__schools">
          <span v-text="getSchoolIdsText(item)" />
          <div
            v-if="hasMessage(item)"
            :class="getMessageClass(item)"
            v-text="item.error_message"
          />
          <v-btn
            v-if="hasHiddenSchoolIds(item)"
            text
            x-small
            class="LKPublicationsCardRecommendationAggregatedTable__more ml-1 px-1"
            @click="toggleExpanded(item)"
          >
            {{ isExpanded(item) ? '...' : `... ${getHiddenSchoolIdsCount(item)}` }}
          </v-btn>
        </div>
      </template>

      <template #[`item.created_at`]="{ item }">
        {{ item.created_at | dateFormatter }}
      </template>

      <template #[`item.finished_at`]="{ item }">
        {{ item.finished_at | dateFormatter }}
      </template>

      <template #[`item.actions`]="{ item }">
        <BTooltipButton
          icon="mdi-download"
          tooltip="Скачать"
          small
          depressed
          bottom
          class-button="LKPublicationsCardRecommendationAggregatedTable__action"
          :disabled="isDownloadDisabled(item)"
          :loading="downloadLoadingId === item.id"
          @click="onDownload(item)"
        />
        <BTooltipButton
          icon="mdi-delete"
          tooltip="Удалить"
          small
          depressed
          bottom
          class-button="LKPublicationsCardRecommendationAggregatedTable__action"
          :loading="deleteLoadingId === item.id"
          @click="onDelete(item)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import BTooltipButton from '@/components/common/BTooltipButton.vue'
import { LkRecomendationApi } from '@/api/resources'
import { download } from '@/helpers/downloadBlob'
import { createModalConfirm } from '@/helpers/createModalConfirm'

const STATUS_LABELS = {
  pending: 'В очереди',
  in_progress: 'Выполняется',
  done: 'Готово',
  failed: 'Ошибка',
}

const PROFILE_TYPE_LABELS = {
  adm: 'Административный',
  reg: 'Региональный',
  sch: 'Школьный',
  avg: 'Средние индексы',
}

const SCHOOL_IDS_COLLAPSED_COUNT = 5
const REFRESH_INTERVAL = 10000

export default {
  name: 'LKPublicationsCardRecommendationAggregatedTable',
  components: {
    BTooltipButton,
  },
  props: {
    publicationId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    items: [],
    expandedIds: [],
    isLoading: false,
    refreshTimerId: null,
    downloadLoadingId: null,
    deleteLoadingId: null,
    statusLabels: STATUS_LABELS,
    profileTypeLabels: PROFILE_TYPE_LABELS,
    footerProps: {
      itemsPerPageText: 'Строк на странице',
    },
    headers: [
      {
        text: 'Статус',
        value: 'status',
        class: 'LKPublicationsCardRecommendationAggregatedTable__status',
        cellClass: 'LKPublicationsCardRecommendationAggregatedTable__status',
      },
      {
        text: 'Тип профиля',
        value: 'profile_type',
        class: 'LKPublicationsCardRecommendationAggregatedTable__profile-type',
        cellClass: 'LKPublicationsCardRecommendationAggregatedTable__profile-type',
      },
      { text: 'Школы', value: 'school_ids', sortable: false },
      { text: 'Создано', value: 'created_at' },
      { text: 'Завершено', value: 'finished_at' },
      {
        text: '',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: 96,
      },
    ],
  }),
  mounted() {
    this.getData()
    this.startRefresh()
  },
  beforeDestroy() {
    this.stopRefresh()
  },
  methods: {
    startRefresh() {
      this.stopRefresh()
      this.refreshTimerId = setInterval(this.getData, REFRESH_INTERVAL)
    },
    stopRefresh() {
      if (!this.refreshTimerId) return
      clearInterval(this.refreshTimerId)
      this.refreshTimerId = null
    },
    async getData() {
      if (this.isLoading) return

      const api = new LkRecomendationApi()
      this.isLoading = true
      try {
        this.items = await api.getAggregatedGenerationStatus(this.publicationId)
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoading = false
      }
    },
    isExpanded(item) {
      return this.expandedIds.includes(item.id)
    },
    isAverage(item) {
      return item.profile_type === 'avg'
    },
    hasMessage(item) {
      return Boolean(item.error_message)
    },
    getMessageClass(item) {
      return item.status === 'failed'
        ? 'LKPublicationsCardRecommendationAggregatedTable__error'
        : 'LKPublicationsCardRecommendationAggregatedTable__warning'
    },
    isDownloadDisabled(item) {
      return item.status !== 'done' || this.isAverage(item)
    },
    hasHiddenSchoolIds(item) {
      return (item.school_ids || []).length > SCHOOL_IDS_COLLAPSED_COUNT
    },
    getHiddenSchoolIdsCount(item) {
      return Math.max(0, (item.school_ids || []).length - SCHOOL_IDS_COLLAPSED_COUNT)
    },
    getSchoolIdsText(item) {
      const schoolIds = item.school_ids || []
      const visibleSchoolIds = this.isExpanded(item)
        ? schoolIds
        : schoolIds.slice(0, SCHOOL_IDS_COLLAPSED_COUNT)

      return visibleSchoolIds.join(', ')
    },
    toggleExpanded(item) {
      if (this.isExpanded(item)) {
        this.expandedIds = this.expandedIds.filter((id) => id !== item.id)
        return
      }

      this.expandedIds = [...this.expandedIds, item.id]
    },
    async onDownload(item) {
      const api = new LkRecomendationApi()
      this.downloadLoadingId = item.id
      try {
        await download(
          api.getAggregatedGenerationDownloadUrl(this.publicationId, item.id),
          item.file_name,
        )
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.downloadLoadingId = null
      }
    },
    async onDelete(item) {
      const modal = await createModalConfirm(this, {
        mainText: 'Вы уверены что хотите удалить эту запись и все связанные файлы?',
        successText: 'Удалено успешно',
        action: async () => {
          const api = new LkRecomendationApi()
          this.deleteLoadingId = item.id
          await api.deleteAggregatedGeneration(this.publicationId, item.id)
          this.items = this.items.filter(({ id }) => id !== item.id)
          this.expandedIds = this.expandedIds.filter((id) => id !== item.id)
        },
      })

      try {
        await modal.open()
      } finally {
        this.deleteLoadingId = null
      }
    },
  },
}
</script>

<style lang="scss">
.LKPublicationsCardRecommendationAggregatedTable {
  max-width: 100%;
  margin-top: 8px;

  &__table {
    font-size: 12px;
    border-radius: 4px;
  }

  &__status {
    width: 96px;
    min-width: 96px;
    white-space: nowrap !important;
  }

  &__profile-type {
    width: 124px;
    min-width: 124px;
    white-space: nowrap !important;
  }

  &__schools {
    max-width: 420px;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__warning,
  &__error {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.35;
    white-space: normal;
  }

  &__warning {
    color: #b26a00;
  }

  &__error {
    color: #b00020;
  }

  &__more {
    min-width: 0 !important;
    height: 22px !important;
    font-size: 11px !important;
    text-transform: none;
  }

  &__action {
    min-width: 28px !important;
    width: 28px;
    height: 28px !important;
    margin-left: 4px;
  }

  .v-data-table__wrapper {
    overflow-x: auto;
  }

  .v-data-table-header th {
    height: 34px !important;
    padding: 0 8px !important;
    font-size: 11px !important;
    white-space: nowrap;
  }

  tbody td {
    height: auto !important;
    min-height: 36px;
    padding: 6px 8px !important;
    vertical-align: top;
    font-size: 12px;
  }
}
</style>
